import { FC, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';

import LocationAutoSelect from '../LocationAutoSelect';
import { useGetAirportsQuery } from '../../store/services/tripsApi';

import styles from './AirportSelect.module.scss';
import { LocationIcon } from '../../components/Icons/LocationIcon';

export type AirportSelection = { name: string; iata_code: string };

interface AirportSelectProps {
  name: string;
  inputLabel: string;
  value: string;
  searchType?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onDetailsChange?: (details: AirportSelection | null) => void;
  onSelectionChange?: (iataCode: string, details: AirportSelection | null) => void;
  error?: string;
  details?: AirportSelection | null;
  showLocationIcon?: boolean;
  reserveErrorSlot?: boolean;
  compactForMobile?: boolean;
  clearable?: boolean;
}

const AirportSelect: FC<AirportSelectProps> = ({
  name,
  inputLabel,
  value,
  searchType,
  onChange,
  onDetailsChange,
  onSelectionChange,
  details,
  error,
  showLocationIcon = false,
  reserveErrorSlot = false,
  compactForMobile = false,
  clearable = true,
}) => {
  const [airportInput, setAirportInput] = useState('');
  const [airportSearch, setAirportSearch] = useState('');

  const { data: airports } = useGetAirportsQuery(
    {
      search: airportSearch,
      ...(searchType ? { type: searchType } : {}),
    },
    {
      skip: !airportSearch || airportSearch.trim() === '',
    }
  );

  useEffect(() => {
    const timeout = setTimeout(() => {
      setAirportSearch(airportInput);
    }, 500);

    return () => clearTimeout(timeout);
  }, [airportInput]);

  const handleChange = (
    _event: React.SyntheticEvent<Element, Event>,
    selectedValue: AirportSelection | null
  ) => {
    const code = selectedValue ? selectedValue.iata_code : '';

    if (onSelectionChange) {
      onSelectionChange(code, selectedValue);
      return;
    }

    if (onChange) {
      const syntheticEvent = {
        target: {
          name,
          value: code,
        },
      } as React.ChangeEvent<HTMLInputElement>;
      onChange(syntheticEvent);
    }

    onDetailsChange?.(selectedValue);
  };

  const trimmed = value?.trim() ?? '';
  const codeNorm = trimmed.toUpperCase();
  const detailsMatch: AirportSelection | null =
    !trimmed || details?.iata_code?.trim().toUpperCase() !== codeNorm ? null : details;
  const fromList: AirportSelection | null =
    !trimmed ? null : airports?.find(a => a.iata_code.trim().toUpperCase() === codeNorm) ?? null;

  const currentAirport: AirportSelection | null =
    !trimmed ? null : detailsMatch ?? fromList ?? { name: codeNorm, iata_code: trimmed };

  return (
    <Box
      className={`${styles.customSelectContainer} ${showLocationIcon ? styles.withLocationLead : ''}`}
      data-invalid={error ? 'true' : undefined}
    >
      <LocationAutoSelect
        options={airports ?? []}
        inputLabel={inputLabel}
        placeholder="Enter airport name"
        handleChange={handleChange}
        invalid={!!error}
        compactForMobile={compactForMobile}
        onInputChange={(event, inputValue) => {
          setAirportInput(inputValue);
        }}
        value={currentAirport || null}
        customClass={styles.customInput}
        startAdornment={
          showLocationIcon ? <LocationIcon className={styles.leadLocationIcon} /> : undefined
        }
        disableClearable={!clearable}
      />
      {reserveErrorSlot ? (
        <Box
          className={styles.errorSlotReserved}
          role={error ? 'alert' : undefined}
        >
          {error ? (
            <Typography
              variant="h12"
              className={styles.errorText}
            >
              {error}
            </Typography>
          ) : null}
        </Box>
      ) : (
        error && (
          <Box
            className={styles.errorSlotInline}
            role="alert"
          >
            <Typography
              variant="h12"
              className={styles.errorText}
            >
              {error}
            </Typography>
          </Box>
        )
      )}
    </Box>
  );
};

export default AirportSelect;
